import { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import { Minus, Plus, Loader2 } from "lucide-react";

export function isLowStock(row) {
  const min = row.minimum_level;
  if (min === undefined || min === null || min === "") return false;
  return Number(row.quantity_available || 0) <= Number(min);
}

export function StockQtyCell({ row, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState("");

  const adjust = async (delta) => {
    if (!delta) return;
    setBusy(true);
    try {
      const wasLow = isLowStock(row);
      const { data } = await api.post(`/stock/${row.id}/adjust`, { delta });
      onChanged(data);
      if (!wasLow && isLowStock(data)) toast.warning(`${data.name || data.ref} is at/below minimum level (${data.quantity_available})`);
    } catch (e) { toast.error(e.response?.data?.detail || "Adjust failed"); }
    finally { setBusy(false); }
  };

  const applyBulk = async (sign) => {
    const n = parseFloat(amount);
    if (!n || n <= 0) { toast.error("Enter an amount"); return; }
    await adjust(sign * n);
    setOpen(false); setAmount("");
  };

  const qty = row.quantity_available ?? 0;
  return (
    <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
      <button data-testid={`stock-dec-${row.id}`} title="Remove 1" disabled={busy || Number(qty) <= 0} onClick={() => adjust(-1)}
        className="h-6 w-6 grid place-items-center rounded border border-slate-200 text-slate-500 hover:bg-slate-100 hover:text-slate-800 disabled:opacity-30 transition-colors">
        <Minus className="h-3 w-3" />
      </button>
      <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) setAmount(""); }}>
        <PopoverTrigger asChild>
          <button data-testid={`stock-qty-${row.id}`} title="Click to adjust by a larger amount"
            className={`min-w-[2.5rem] px-1.5 h-6 rounded text-center font-semibold tabular-nums transition-colors ${isLowStock(row) ? "text-amber-700 bg-amber-100" : "text-slate-800 hover:bg-slate-100"}`}>
            {busy ? <Loader2 className="h-3 w-3 animate-spin inline" /> : qty}
          </button>
        </PopoverTrigger>
        <PopoverContent className="bg-white w-56 p-3" align="start" onClick={(e) => e.stopPropagation()}>
          <p className="text-xs font-medium text-slate-600 mb-2">Adjust {row.name || row.ref}</p>
          <Input type="number" min="0" autoFocus data-testid={`stock-adjust-amount-${row.id}`} placeholder="Amount e.g. 25"
            value={amount} onChange={(e) => setAmount(e.target.value)} className="h-8 mb-2 bg-white"
            onKeyDown={(e) => { if (e.key === "Enter") applyBulk(1); }} />
          <div className="flex gap-2">
            <Button size="sm" className="btn-accent flex-1 h-8" data-testid={`stock-adjust-add-${row.id}`} disabled={busy} onClick={() => applyBulk(1)}>Add</Button>
            <Button size="sm" variant="outline" className="flex-1 h-8 text-red-600 hover:text-red-700" data-testid={`stock-adjust-remove-${row.id}`} disabled={busy} onClick={() => applyBulk(-1)}>Remove</Button>
          </div>
          <p className="text-[10px] text-slate-400 mt-2">Quantity can't go below 0. All changes are audited.</p>
        </PopoverContent>
      </Popover>
      <button data-testid={`stock-inc-${row.id}`} title="Add 1" disabled={busy} onClick={() => adjust(1)}
        className="h-6 w-6 grid place-items-center rounded border border-slate-200 text-slate-500 hover:bg-slate-100 hover:text-slate-800 disabled:opacity-30 transition-colors">
        <Plus className="h-3 w-3" />
      </button>
    </div>
  );
}
